import React from 'react';
import {
  Typography,
  Chip,
  Box,
  Alert,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  alpha,
} from '@mui/material';
import { format } from 'date-fns';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import StarIcon from '@mui/icons-material/Star';
import InfoIcon from '@mui/icons-material/Info';

type RaceStatus = 'counting' | 'replaced' | 'not_counted';

interface RaceLimitInfo {
  min_races: number;
  total_eligible: number;
  this_race_rank?: number;
}

interface Race {
  race_id: number;
  track_name: string;
  event_date: string;
  finish_position: number | null;
  points_earned: number | null;
  points_counted?: boolean;
  is_replaced?: boolean;
  is_weekly_best?: boolean;
  status?: RaceStatus;
  status_reason?: string;
  race_type?: string;
  rule_type?: 'highest_only' | 'all_count' | null;
  week_number?: number;
  show_week_header?: boolean;
  race_limit_info?: RaceLimitInfo | null;
}

interface RaceDetailsListProps {
  races: Race[];
  totalPoints?: number;
}

const STATUS_ICON: Record<RaceStatus, React.ReactElement> = {
  counting: <CheckCircleIcon color="success" sx={{ fontSize: 18 }} />,
  replaced: <SwapHorizIcon color="warning" sx={{ fontSize: 18 }} />,
  not_counted: <CancelIcon color="error" sx={{ fontSize: 18 }} />,
};

const STATUS_CHIP_COLOR: Record<RaceStatus, 'success' | 'warning' | 'error'> = {
  counting: 'success',
  replaced: 'warning',
  not_counted: 'error',
};

const UNKNOWN_STATUS_ICON = <InfoIcon color="info" sx={{ fontSize: 18 }} />;

const statusIconFor = (race: Race) =>
  (race.status && STATUS_ICON[race.status]) || UNKNOWN_STATUS_ICON;

const statusChipColorFor = (race: Race): 'success' | 'warning' | 'error' | 'default' =>
  (race.status && STATUS_CHIP_COLOR[race.status]) || 'default';

const statusLabelFor = (race: Race): string => {
  if (race.status === 'counting') return `${race.points_earned || 0} pts`;
  if (race.status === 'replaced') return `${race.points_earned || 0} pts (Replaced)`;
  if (race.status === 'not_counted') return 'Not Counted';
  return 'Unknown';
};

const formatRaceType = (raceType?: string): string => {
  if (!raceType) return '';
  return raceType
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

function RaceDetailsList({ races, totalPoints }: RaceDetailsListProps): React.ReactElement {
  const countedRaces = races.filter(race => race.points_counted);
  const notCountedRaces = races.filter(race => !race.points_counted);
  const replacedRaces = races.filter(race => race.is_replaced);

  const calculatedTotal = totalPoints ?? races.reduce(
    (sum, race) => sum + (race.points_counted ? (race.points_earned || 0) : 0),
    0
  );

  return (
    <Box>
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
          <Chip
            icon={<CheckCircleIcon />}
            label={`${countedRaces.length} Counting`}
            color="success"
            size="small"
            variant="outlined"
          />
          {replacedRaces.length > 0 && (
            <Chip
              icon={<SwapHorizIcon />}
              label={`${replacedRaces.length} Replaced`}
              color="warning"
              size="small"
              variant="outlined"
            />
          )}
          {notCountedRaces.length > 0 && (
            <Chip
              icon={<CancelIcon />}
              label={`${notCountedRaces.length} Not Counted`}
              color="error"
              size="small"
              variant="outlined"
            />
          )}
        </Box>
        <Chip
          label={`Total: ${calculatedTotal} pts`}
          color="primary"
          sx={{ fontWeight: 600, fontSize: '0.875rem' }}
        />
      </Box>

      {races.length > 0 && races[0].race_limit_info && (
        <Alert severity="info" sx={{ mb: 2, py: 0.5 }} icon={<InfoIcon fontSize="small" />}>
          <Typography variant="body2">
            Best {races[0].race_limit_info.min_races} of {races[0].race_limit_info.total_eligible} eligible races count toward standings.
            {replacedRaces.length > 0 && (
              <> {replacedRaces.length} race{replacedRaces.length !== 1 ? 's' : ''} replaced by better finishes.</>
            )}
          </Typography>
        </Alert>
      )}

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 50 }} />
              <TableCell>Date</TableCell>
              <TableCell>Track</TableCell>
              <TableCell>Type</TableCell>
              <TableCell align="center">Pos</TableCell>
              <TableCell align="right">Points</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {races.map((race, index) => (
              <React.Fragment key={race.race_id}>
                {race.show_week_header && (
                  <TableRow>
                    <TableCell colSpan={6} sx={{ py: 0.75, px: 1, border: 'none', ...(index > 0 ? { pt: 2 } : {}) }}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.75,
                          px: 1,
                          py: 0.5,
                          backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.06),
                          border: '1px solid',
                          borderColor: (theme) => alpha(theme.palette.primary.main, 0.18),
                          borderRadius: 1,
                        }}
                      >
                        <StarIcon color="primary" sx={{ fontSize: 14 }} />
                        <Typography
                          variant="caption"
                          color="primary.main"
                          sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}
                        >
                          Week {race.week_number} · Best Result Only
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                )}
                <TableRow
                  sx={{
                    opacity: race.points_counted ? 1 : 0.65,
                    '& td': { borderColor: 'grey.100' },
                  }}
                >
                  <TableCell sx={{ width: 50, pr: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Tooltip title={race.status_reason || ''}>{statusIconFor(race)}</Tooltip>
                      {race.is_weekly_best && (
                        <Tooltip title="Best result this week">
                          <StarIcon color="primary" sx={{ fontSize: 14 }} />
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {format(new Date(race.event_date), 'MMM d, yyyy')}
                  </TableCell>
                  <TableCell>{race.track_name}</TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {formatRaceType(race.race_type)}
                    </Typography>
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 500, fontVariantNumeric: 'tabular-nums' }}>
                    {race.finish_position ?? '-'}
                  </TableCell>
                  <TableCell align="right">
                    <Chip
                      label={statusLabelFor(race)}
                      color={statusChipColorFor(race)}
                      size="small"
                      variant={race.points_counted ? 'filled' : 'outlined'}
                      sx={{
                        fontWeight: 600,
                        fontSize: '0.75rem',
                        height: 22,
                        ...(!race.points_counted && race.status !== 'replaced'
                          ? { textDecoration: 'line-through', opacity: 0.8 }
                          : {}),
                      }}
                    />
                  </TableCell>
                </TableRow>
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

export default RaceDetailsList;
